import type {
  ReconciliationDetail,
  Statement,
  StatementSegment,
} from '../stores/types'
import { needsReview } from './reconciliation'
import { CHECKS_KIND } from './statement_segment'

// A file whose checks the engine flagged, often for pages it could not read. `unreadPages` is
// empty when no cycle was flagged for unread pages; `reasons` then says why.
export interface FlaggedCheckFile {
  statementId: number
  fileName: string
  numberOfPages: number
  unreadPages: number[]
  reasons: string[]
}

export const CHECK_PAGES_UNREAD_REASON = 'check_packet_unread'

type ReviewWhy = NonNullable<ReconciliationDetail['why']>

// A bank cycle can carry unread check pages too, when the engine did not split them out.
const isChecksCycleOrUnreadPages = (segment: StatementSegment) =>
  segment.kind?.toLowerCase() === CHECKS_KIND ||
  segment.reconciliationDetail?.why?.reason === CHECK_PAGES_UNREAD_REASON

// Keyed on the verdict, not on `why`: accepting a cycle keeps its `why` but clears needs_review.
export const isFlaggedChecksCycle = (segment: StatementSegment) =>
  isChecksCycleOrUnreadPages(segment) && needsReview(segment)

// Other reasons also name pages (a chain break's pages); only this one means the pages were unread.
const unreadPagesOf = (why: ReviewWhy) =>
  why.reason === CHECK_PAGES_UNREAD_REASON ? (why.pages ?? []) : []

const uniqueSorted = (pages: number[]) =>
  [...new Set(pages)].sort((first, second) => first - second)

const toFlaggedCheckFile = (
  statement: Statement,
  flaggedCycles: StatementSegment[],
): FlaggedCheckFile => {
  const whys = flaggedCycles.map(
    (segment): ReviewWhy => segment.reconciliationDetail?.why ?? {},
  )
  return {
    statementId: statement.id,
    fileName: statement.fileName,
    numberOfPages: statement.numberOfPages,
    unreadPages: uniqueSorted(whys.flatMap(unreadPagesOf)),
    reasons: [
      ...new Set(whys.flatMap((why) => (why.reason ? [why.reason] : []))),
    ],
  }
}

// Every file, not only check files: a mixed file holds checks cycles too.
export const flaggedCheckFiles = (
  statements: Statement[],
): FlaggedCheckFile[] =>
  statements.flatMap((statement) => {
    const flaggedCycles = (statement.segments ?? []).filter(
      isFlaggedChecksCycle,
    )
    if (flaggedCycles.length === 0) return []
    return [toFlaggedCheckFile(statement, flaggedCycles)]
  })
